/**
 * The project page's code chunk (overview, plan, team, Needs you, item, goal, KPI and search pages). It is a lazy route
 * chunk, so Home and sign-in don't load it at boot (it carries most of the app's page code). One loader for the
 * router's `lazyRouteComponent` and for the early loads:
 *
 * - a project URL opened directly: index.html already modulepreloads the chunk and its imports (`dev/route-preload.ts`),
 *   and `main` imports it at once, in parallel with the session and the snapshot, instead of after the router matched;
 * - Home and the other workspace pages (and `/`, which goes to one) fetch it a little after the page has loaded and
 *   its first data had the network to itself, so a tap on a project finds it in the cache (the router's intent preload
 *   also starts it on hover and touchstart). Sign-in, invite and OAuth pages don't.
 */
export const loadProjectPages = () => import("@/routes/project")

const PROJECT_PATH = /^\/w\/[^/]+\/p\/[^/]+/

/** How long after the page's `load` the chunk is fetched (then when the browser is idle). */
export const WARM_AFTER_LOAD_MS = 3_000

export const warmProjectChunk = (
  pathname: string,
  load: () => Promise<unknown> = loadProjectPages,
  later: (run: () => void) => void = afterLoadAndIdle,
): void => {
  const start = () => {
    load().catch(() => {}) // a failure is left to the route's own load (it retries and shows the error)
  }
  if (PROJECT_PATH.test(pathname)) start()
  else if (pathname === "/" || pathname.startsWith("/w/")) later(start)
}

const afterLoadAndIdle = (run: () => void): void => {
  const idle = () => {
    const ric = (globalThis as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number })
      .requestIdleCallback
    if (ric) ric(run, { timeout: WARM_AFTER_LOAD_MS })
    else run() // Safari has no requestIdleCallback
  }
  const wait = () => setTimeout(idle, WARM_AFTER_LOAD_MS)
  if (typeof document === "undefined" || document.readyState === "complete") wait()
  else window.addEventListener("load", wait, { once: true })
}
